import React from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { logErrorToServer } from '../../appCall';
import { v4 as uuidv4 } from 'uuid';

// Define the interface for error log data expected by your logErrorToServer function
interface ErrorLogData {
  type: string;
  error: string;
  stack?: string;
  componentStack?: string;
}

// Define the expected props for the ErrorBoundary
interface ErrorBoundaryProps {
  children: ReactNode;
}

// Define the internal state
interface ErrorBoundaryState {
  hasError: boolean;
  errorId?: string;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const id = uuidv4();
    const errorData: ErrorLogData = {
      type: 'component_error_' + id,
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined
    };

    // Send error log
    logErrorToServer(errorData);
    this.setState({ hasError: true, errorId: id });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div>
          <h2>Something went wrong in the UI.</h2>
          {this.state.errorId && (
            <p>
              Error reference: <code>{this.state.errorId}</code>
            </p>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
